import React, { useState, useEffect, useRef } from 'react';
import api from './api';

const NotificationDropdown = ({ onViewProfile, onViewThread }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Poll unread count every 60s
  useEffect(() => {
    const fetchCount = () => {
      api.get('/notifications/unread-count')
        .then(res => setUnreadCount(res.data.count))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Load notifications when opened
  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get('/notifications?limit=20')
        .then(res => setNotifications(res.data.items))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = () => {
    api.post('/notifications/read-all').then(() => {
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    }).catch(() => {});
  };

  const markRead = (ids) => {
    api.post('/notifications/read', { notificationIds: ids }).catch(() => {});
  };

  const handleClick = (notif) => {
    if (!notif.is_read) {
      markRead([notif.id]);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    if (notif.type === 'follow' && onViewProfile) {
      onViewProfile(notif.actor_id);
    } else if ((notif.type === 'thread_comment') && onViewThread) {
      onViewThread(notif.entity_id);
    }
    setOpen(false);
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getNotifText = (notif) => {
    const name = notif.actor_first_name || notif.actor_name || 'Someone';
    switch (notif.type) {
      case 'follow':
        return `${name} started following you`;
      case 'thread_comment':
        return `${name} commented on your thread${notif.metadata?.thread_title ? `: "${notif.metadata.thread_title}"` : ''}`;
      case 'cheer':
      case 'clap':
        return `${name} cheered on your ${notif.entity_type || 'post'}`;
      case 'feed_comment':
        return `${name} commented on your ${notif.entity_type || 'post'}`;
      case 'strategy_share':
        return `${name} shared their strategy${notif.metadata?.race_name ? ` for ${notif.metadata.race_name}` : ''}`;
      default:
        return `${name} interacted with your content`;
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1.25rem',
          padding: '0.4rem',
          color: open ? '#FF8A3D' : '#8b9cb3',
          transition: 'color 0.2s ease',
          position: 'relative',
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: '#ef4444',
            color: '#fff',
            fontSize: '0.65rem',
            fontWeight: 700,
            borderRadius: '50%',
            minWidth: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 3px',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '0.5rem',
          background: '#16161D',
          border: '1px solid rgba(255, 138, 61, 0.2)',
          borderRadius: '12px',
          minWidth: '320px',
          maxHeight: '400px',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          zIndex: 100,
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem 1rem',
            borderBottom: '1px solid rgba(255, 138, 61, 0.1)',
          }}>
            <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.95rem' }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#FF8A3D',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#8b9cb3' }}>Loading...</div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#8b9cb3', fontSize: '0.9rem' }}>
              No notifications yet
            </div>
          ) : (
            notifications.map(notif => (
              <button
                key={notif.id}
                onClick={() => handleClick(notif)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  width: '100%',
                  background: notif.is_read ? 'transparent' : 'rgba(255, 138, 61, 0.05)',
                  border: 'none',
                  borderBottom: '1px solid rgba(255, 138, 61, 0.06)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: '#252530',
                  flexShrink: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {notif.actor_photo ? (
                    <img src={notif.actor_photo} alt={`${notif.actor_first_name || notif.actor_name || 'User'} avatar`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ color: '#8b9cb3', fontSize: '0.8rem' }}>
                      {(notif.actor_first_name || notif.actor_name || '?')[0]}
                    </span>
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: notif.is_read ? '#8b9cb3' : '#c4cdd9',
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                  }}>
                    {getNotifText(notif)}
                  </div>
                  <div style={{ color: '#6b7a8d', fontSize: '0.75rem', marginTop: '0.2rem' }}>
                    {formatTime(notif.created_at)}
                  </div>
                </div>

                {/* Unread dot */}
                {!notif.is_read && (
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#FF8A3D',
                    flexShrink: 0,
                    marginTop: '0.3rem',
                  }} />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
